import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../store/actions/index'; // Pfad ggf. anpassen
// Importiere die neue Action
import { useParams } from "react-router";
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { currentLang } from '../../store/actions/support'; // Pfad ggf. anpassen
import debounce from 'lodash/debounce'; // Importiere debounce


// --- Hilfsfunktionen (safeParseFloat, formatNumber, calculateRowSum) ---
const safeParseFloat = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const stringValue = String(value);
    const parsed = parseFloat(stringValue.replace(',', '.'));
    return isNaN(parsed) ? 0 : parsed;
};
const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || value === '' || isNaN(value)) { return "0".concat(",", "0".repeat(decimals)); }
    try { return safeParseFloat(value).toFixed(decimals).replace('.', ','); }
    catch (e) { console.error("Formatierungsfehler:", e); return "0".concat(",", "0".repeat(decimals)); }
};
const calculateRowSum = (rounded_verkaufseinheit, price, discount) => {
    try {
        const units = safeParseFloat(rounded_verkaufseinheit); const unitPrice = safeParseFloat(price); const discountPercent = safeParseFloat(discount);
        const result = (units * unitPrice / 100) * (100 - discountPercent); // Originalformel beibehalten
        return isNaN(result) || !isFinite(result) ? 0 : result;
    } catch (e) { console.error("Berechnungsfehler:", e); return 0; }
};


// --- SafeNumber Komponente ---
const SafeNumber = ({ value, decimals = 2, style = {} }) => {
    const formattedValue = formatNumber(value, decimals);
    return <span style={style}>{formattedValue}</span>;
};
SafeNumber.propTypes = { value: PropTypes.any, decimals: PropTypes.number, style: PropTypes.object };


// --- usePositionsManager Hook ---
// (Unverändert mit Korrekturen)
const usePositionsManager = (positions, projectId) => {
    const dispatch = useDispatch();
    const [localValues, setLocalValues] = useState({});
    const initializedRef = useRef(false);
    const previousProjectIdRef = useRef(projectId);
    const positionsHashRef = useRef('');

    const createPositionsHash = useCallback((currentPositions) => {
        if (!Array.isArray(currentPositions)) return '';
        return currentPositions.map(p =>
          `${p.id}_${p.rounded_verkaufseinheit != null ? p.rounded_verkaufseinheit : ''}_${p.price != null ? p.price : ''}_${p.discount != null ? p.discount : ''}`
        ).join('|');
    }, []);

    useEffect(() => {
        const currentPositionsHash = createPositionsHash(positions);
        const projectIdChanged = projectId !== previousProjectIdRef.current;

        if (Array.isArray(positions) && positions.length > 0) {
            const positionsContentChanged = currentPositionsHash !== positionsHashRef.current;

            if (projectIdChanged || positionsContentChanged || !initializedRef.current) {
                const initialValues = {};
                const updates = [];
                positions.forEach((position) => {
                    const rounded_verkaufseinheit = position.rounded_verkaufseinheit != null ? position.rounded_verkaufseinheit : '';
                    const price = position.price != null ? position.price : '';
                    const discount = position.discount != null ? position.discount : '';
                    initialValues[`rounded_verkaufseinheit-${position.id}`] = rounded_verkaufseinheit;
                    initialValues[`price-${position.id}`] = price;
                    initialValues[`discount-${position.id}`] = discount;
                    const rowsum = calculateRowSum(rounded_verkaufseinheit, price, discount);
                    initialValues[`rowsum-${position.id}`] = rowsum;
                    if (position.rowsum != null && position.rowsum !== rowsum) {
                        updates.push({ id: position.id, rounded_verkaufseinheit, price, discount, rowsum });
                    }
                });
                setLocalValues(initialValues);
                initializedRef.current = true;
                positionsHashRef.current = currentPositionsHash;
                if (updates.length > 0 && (!projectIdChanged || !initializedRef.current )) {
                    // dispatch(actions.updateProjectOfferPositions(projectId, updates));
                }
            }
        } else {
            if (initializedRef.current || projectIdChanged) {
                setLocalValues({});
                initializedRef.current = false;
                positionsHashRef.current = '';
            }
        }
        if (projectIdChanged) {
            previousProjectIdRef.current = projectId;
        }
    }, [positions, projectId, dispatch, createPositionsHash]);

    const debouncedUpdate = useRef(debounce((pId, posData) => {
        dispatch(actions.updateProjectOfferPositions(pId, [posData]));
    }, 1000)).current;

    const getValue = useCallback((positionId, field) => {
        const localKey = `${field}-${positionId}`;
        if (localValues[localKey] != null) {
            return localValues[localKey];
        }
        const position = Array.isArray(positions) ? positions.find(p => p.id === positionId) : undefined;
        return (position && position[field] != null) ? position[field] : '';
    }, [localValues, positions]);

    const handleChange = useCallback((positionId, field, newValue) => {
        const position = Array.isArray(positions) ? positions.find(p => p.id === positionId) : undefined;
        if (!position) return;
        setLocalValues(prev => {
            const newLocalValues = {...prev};
            newLocalValues[`${field}-${positionId}`] = newValue;
            const currentRoundedVerkaufseinheit = field === 'rounded_verkaufseinheit' ? newValue : getValue(positionId, 'rounded_verkaufseinheit');
            const currentPrice = field === 'price' ? newValue : getValue(positionId, 'price');
            const currentDiscount = field === 'discount' ? newValue : getValue(positionId, 'discount');
            const rowsum = calculateRowSum(currentRoundedVerkaufseinheit, currentPrice, currentDiscount);
            newLocalValues[`rowsum-${positionId}`] = rowsum;
            const updateData = { id: positionId, rounded_verkaufseinheit: safeParseFloat(currentRoundedVerkaufseinheit), price: safeParseFloat(currentPrice), discount: safeParseFloat(currentDiscount), rowsum };
            debouncedUpdate(projectId, updateData);
            return newLocalValues;
        });
    }, [getValue, projectId, debouncedUpdate, positions]);

    const totals = useMemo(() => {
        if (!Array.isArray(positions) || positions.length === 0) {
            return { totalSum: 0, totalDiscount: 0 };
        }
        try {
            let withDiscount = 0; let withoutDiscount = 0;
            positions.forEach(position => {
                const units = safeParseFloat(getValue(position.id, 'rounded_verkaufseinheit'));
                const price = safeParseFloat(getValue(position.id, 'price'));
                const discount = safeParseFloat(getValue(position.id, 'discount'));
                withDiscount += calculateRowSum(units, price, discount);
                withoutDiscount += (units * price);
            });
            const discountPercentage = withoutDiscount === 0 ? 0 : (1 - (withDiscount / withoutDiscount)) * 100;
            return { totalSum: isNaN(withDiscount) ? 0 : withDiscount, totalDiscount: isNaN(discountPercentage) ? 0 : discountPercentage };
        } catch (e) { console.error("Fehler bei Gesamtsummenberechnung:", e); return { totalSum: 0, totalDiscount: 0 }; }
    }, [positions, getValue]);

    useEffect(() => { return () => debouncedUpdate.cancel(); }, [debouncedUpdate]);

    return { getValue, handleChange, totals };
};


// --- Styles ---
const uniformInputWidth = '80px';
const uniformVerticalPadding = '0.375rem';
const uniformHorizontalPaddingText = '0.75rem';
const uniformHorizontalPaddingNumber = '5px';
const numberCellStyle = { textAlign: 'right', paddingRight: '20px' };
const inputCellWithOwnAlignmentStyle = { paddingRight: '20px', verticalAlign: 'top', textAlign: 'right' };
const baseInputStyle = { border: '1px solid #e2e2e2', borderRadius: '3px', fontSize: 'inherit', lineHeight: 'normal', boxSizing: 'border-box', paddingTop: uniformVerticalPadding, paddingBottom: uniformVerticalPadding };
const costInputStyle = { ...baseInputStyle, width: uniformInputWidth, textAlign: 'right', paddingLeft: uniformHorizontalPaddingNumber, paddingRight: uniformHorizontalPaddingNumber };
const descriptionInputStyle = { ...baseInputStyle, marginRight: '10px', flexGrow: 1, paddingLeft: uniformHorizontalPaddingText, paddingRight: uniformHorizontalPaddingText };
const rowsumCellStyle = { ...numberCellStyle, fontWeight: 'bold' };
const iconSpanStyle = { width: "40px", cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const iconStyle = { fontSize: '24px' };
const tableCellStyleForTextLeft = { verticalAlign: 'top' };


// --- PositionRow Komponente ---
const PositionRow = ({ position, index, getValue, handleChange }) => {
    const rowsumValue = getValue(position.id, 'rowsum');
    return (
        <tr key={position.id} className="small">
            <td>{index + 1}</td>
            <td style={{maxWidth: '200px'}}><strong>{position.name && position.name[currentLang()] ? position.name[currentLang()] : 'N/A'}</strong><br /><small>{position.articlenumber} {position.subline && position.subline[currentLang()] ? `(${position.subline[currentLang()]})` : ''}</small></td>
            <td style={numberCellStyle}>{formatNumber(position.calculated_verkaufseinheit, 1)}</td>
            <td style={inputCellWithOwnAlignmentStyle}> <input type="number" min="0" name="rounded_verkaufseinheit" value={getValue(position.id, 'rounded_verkaufseinheit')} onChange={(e) => handleChange(position.id, 'rounded_verkaufseinheit', e.target.value)} style={costInputStyle} /></td>
            <td style={inputCellWithOwnAlignmentStyle}> <input type="number" min="0" step="0.01" name="price" value={getValue(position.id, 'price')} onChange={(e) => handleChange(position.id, 'price', e.target.value)} style={costInputStyle} /></td>
            <td style={inputCellWithOwnAlignmentStyle}> <input type="number" min="0" max="100" step="0.01" name="discount" value={getValue(position.id, 'discount')} onChange={(e) => handleChange(position.id, 'discount', e.target.value)} style={costInputStyle} /></td>
            <td style={rowsumCellStyle}><SafeNumber value={rowsumValue} /></td>
        </tr>
    );
};
PositionRow.propTypes = { position: PropTypes.object.isRequired, index: PropTypes.number.isRequired, getValue: PropTypes.func.isRequired, handleChange: PropTypes.func.isRequired };


// --- groupPositionsByAlphasort Funktion ---
const groupPositionsByAlphasort = (positionsArray) => {
    if (!Array.isArray(positionsArray)) return {};
    const sorted = [...positionsArray].sort((a, b) => { const sortA = a.alphasort || ''; const sortB = b.alphasort || ''; return sortA.localeCompare(sortB); });
    return sorted.reduce((groups, pos) => { const key = pos.alphasort || 'Andere'; if (!groups[key]) groups[key] = []; groups[key].push(pos); return groups; }, {});
};


// --- Hauptkomponente: ProjectOfferPositions ---
const ProjectOfferPositions = ({ projectId }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // --- Redux Selectors ---
    const projectFromState = useSelector(state => actions.getProject(state, projectId));
    const project = useMemo(() => projectFromState || {}, [projectFromState]);
    // console.log("Project Data from Redux:", project); // DEBUG: Projektobjekt prüfen

    const projectOfferPositionsFromState = useSelector(state =>
        project.project_offer_positions && Array.isArray(project.project_offer_positions)
            ? actions.getInventoryObjects(state, 'projectOfferPositions', project.project_offer_positions)
            : []
    );
    const positions = useMemo(() => (Array.isArray(projectOfferPositionsFromState) ? projectOfferPositionsFromState : []), [projectOfferPositionsFromState]);

    // --- Positions-Management Hook ---
    const { getValue, handleChange, totals } = usePositionsManager(positions, projectId);

    // --- Gruppierung für Anzeige ---
    const groupedPositions = useMemo(() => groupPositionsByAlphasort(positions), [positions]);
    const groupOrder = useMemo(() => ['mineralfaserplatten', 'moertel', 'beschichtung', 'fuellstoff', 'manschette', 'bandage', 'fremdprodukt'], []);
    const sortedGroupEntries = useMemo(() => {
        const entries = Object.entries(groupedPositions);
        return entries.sort(([aKey], [bKey]) => { const idxA = groupOrder.indexOf(aKey); const idxB = groupOrder.indexOf(bKey); if (idxA === -1 && idxB === -1) return aKey.localeCompare(bKey); if (idxA === -1) return 1; if (idxB === -1) return -1; return idxA - idxB; });
    }, [groupedPositions, groupOrder]);

    // --- Lokaler State für Kalkulationsdetails ---
    const [stundenverrechnungssatz, setStundenverrechnungssatz] = useState(0);
    const [arbeitszeit, setArbeitszeit] = useState(0);
    const [baustellennebenkosten, setBaustellennebenkosten] = useState([{ bezeichnung: '', kosten: 0 }]);
    const [sonstiges, setSonstiges] = useState([{ bezeichnung: '', kosten: 0 }]);

    // Ref, um zu speichern, ob die *initiale* Befüllung aus dem Projekt stattgefunden hat
    const initialDataSetRef = useRef(false);
    // Ref, um die vorherige Projekt-ID zu speichern (nur für den Initialisierungs-Hook)
    const initLogicPreviousProjectIdRef = useRef(null); // KORREKTUR: Definiert

    // --- Debounced Update Funktion für Kalkulationsdetails ---
    const debouncedUpdateDetails = useRef(
        debounce((pId, detailsPayload) => {
            // Nur senden, wenn die initiale Befüllung stattgefunden hat
            if (initialDataSetRef.current) {
                dispatch(actions.updateProjectOfferCalculationDetails(pId, detailsPayload));
                // console.log("Dispatching updateProjectOfferCalculationDetails for projectId:", pId); // DEBUG
            } else {
                // console.log("Skipping debounced update because initial data not set yet."); // DEBUG
            }
        }, 1500)
    ).current;

    // Aufräumen des Debounce-Timers beim Unmounten
    useEffect(() => { return () => { debouncedUpdateDetails.cancel(); }; }, [debouncedUpdateDetails]);


    // --- Effekt zur Initialisierung der Kalkulationsdetails (ÜBERARBEITET) ---
    useEffect(() => {
        if (project && project.id) {
            const detailsAvailable = project.project_offer_calculation_details != null;
            const projectIdChanged = initLogicPreviousProjectIdRef.current !== project.id;

            // Initialisiere/Resette, wenn Projekt-ID sich geändert hat ODER
            // wenn es der erste Lauf für dieses Projekt ist UND Daten jetzt verfügbar sind
            if (projectIdChanged || (!initialDataSetRef.current && detailsAvailable)) {
                 // console.log(`ProjectOfferPositions: Initializing/Resetting calculation details for projectId: ${project.id}`); // DEBUG

                if (detailsAvailable) {
                    const details = project.project_offer_calculation_details;
                    setStundenverrechnungssatz(details.hourly_rate_in_cents != null ? details.hourly_rate_in_cents / 100 : 0);
                    setArbeitszeit(details.working_time_in_minutes != null ? details.working_time_in_minutes : 0);
                    setBaustellennebenkosten(
                        details.site_costs && details.site_costs.length > 0
                            ? details.site_costs.map(cost => ({ bezeichnung: cost.description || '', kosten: cost.amount_in_cents != null ? cost.amount_in_cents / 100 : 0 }))
                            : [{ bezeichnung: '', kosten: 0 }]
                    );
                    setSonstiges(
                        details.other_costs && details.other_costs.length > 0
                            ? details.other_costs.map(cost => ({ bezeichnung: cost.description || '', kosten: cost.amount_in_cents != null ? cost.amount_in_cents / 100 : 0 }))
                            : [{ bezeichnung: '', kosten: 0 }]
                    );
                    // Markieren, dass Daten für dieses Projekt gesetzt wurden
                    initialDataSetRef.current = true;
                } else {
                    // Nur zurücksetzen, wenn sich die Projekt-ID geändert hat
                    if (projectIdChanged) {
                        // console.log(`ProjectOfferPositions: Resetting details because projectId changed and no details found for ${project.id}`); // DEBUG
                        setStundenverrechnungssatz(0);
                        setArbeitszeit(0);
                        setBaustellennebenkosten([{ bezeichnung: '', kosten: 0 }]);
                        setSonstiges([{ bezeichnung: '', kosten: 0 }]);
                        initialDataSetRef.current = false; // Keine Daten gesetzt
                    }
                }
                // Merke die aktuelle Projekt-ID für den nächsten Durchlauf
                initLogicPreviousProjectIdRef.current = project.id;
            }
        }
    }, [project]); // Abhängigkeit nur von project


    // --- Berechnungen ---
    const lohnkosten = useMemo(() => (safeParseFloat(stundenverrechnungssatz) * safeParseFloat(arbeitszeit)) / 60, [stundenverrechnungssatz, arbeitszeit]);
    const montagekosten = useMemo(() => {
        const nebenkostenSumme = baustellennebenkosten.reduce((sum, item) => sum + (safeParseFloat(item.kosten) || 0), 0);
        const sonstigesSumme = sonstiges.reduce((sum, item) => sum + (safeParseFloat(item.kosten) || 0), 0);
        return lohnkosten + nebenkostenSumme + sonstigesSumme;
    }, [lohnkosten, baustellennebenkosten, sonstiges]);
    const gesamtkosten = useMemo(() => totals.totalSum + montagekosten, [totals.totalSum, montagekosten]);

    // --- Helper Funktion zum Erstellen des Payloads und Triggern des Updates ---
    // Wird von den Handlern aufgerufen
    const prepareAndUpdate = useCallback((newState) => {
        // Nur updaten, wenn das Projekt geladen ist UND die initiale Befüllung erfolgt ist
        if (!project || !project.id || !initialDataSetRef.current) {
            // console.log("Skipping prepareAndUpdate", { projectLoaded: !!project?.id, initialDataSet: initialDataSetRef.current }); // DEBUG
            return;
        }

        const calculationDetailsPayload = {
            hourly_rate_in_cents: Math.round(safeParseFloat(newState.stundenverrechnungssatz) * 100),
            working_time_in_minutes: safeParseFloat(newState.arbeitszeit),
            site_costs: newState.baustellennebenkosten
                .filter(item => item.bezeichnung.trim() !== '' || safeParseFloat(item.kosten) !== 0)
                .map(item => ({ description: item.bezeichnung, amount_in_cents: Math.round(safeParseFloat(item.kosten) * 100) })),
            other_costs: newState.sonstiges
                .filter(item => item.bezeichnung.trim() !== '' || safeParseFloat(item.kosten) !== 0)
                .map(item => ({ description: item.bezeichnung, amount_in_cents: Math.round(safeParseFloat(item.kosten) * 100) }))
        };
        debouncedUpdateDetails(projectId, calculationDetailsPayload);
    }, [projectId, debouncedUpdateDetails, project]); // Abhängigkeiten: projectId, debouncedUpdateDetails, project


    // --- Handler für Kalkulationsdetails (rufen jetzt prepareAndUpdate auf) ---
    const handleStundenverrechnungssatzChange = (e) => {
        const newValue = safeParseFloat(e.target.value);
        setStundenverrechnungssatz(newValue);
        // Übergebe den *neuen* Wert direkt an prepareAndUpdate
        prepareAndUpdate({
            stundenverrechnungssatz: newValue,
            arbeitszeit, // Aktueller State für andere Werte
            baustellennebenkosten,
            sonstiges
        });
    };
    const handleArbeitszeitChange = (e) => {
        const newValue = safeParseFloat(e.target.value);
        setArbeitszeit(newValue);
        prepareAndUpdate({
            stundenverrechnungssatz,
            arbeitszeit: newValue, // Neuer Wert
            baustellennebenkosten,
            sonstiges
        });
    };
    const handleBaustellennebenkostenChange = (index, field, value) => {
        const newValue = field === 'kosten' ? safeParseFloat(value) : value;
        const newKosten = [...baustellennebenkosten];
        newKosten[index] = { ...newKosten[index], [field]: newValue };
        setBaustellennebenkosten(newKosten);
        prepareAndUpdate({
            stundenverrechnungssatz,
            arbeitszeit,
            baustellennebenkosten: newKosten, // Neues Array
            sonstiges
        });
    };
    const handleSonstigesChange = (index, field, value) => {
        const newValue = field === 'kosten' ? safeParseFloat(value) : value;
        const newSonstiges = [...sonstiges];
        newSonstiges[index] = { ...newSonstiges[index], [field]: newValue };
        setSonstiges(newSonstiges);
        prepareAndUpdate({
            stundenverrechnungssatz,
            arbeitszeit,
            baustellennebenkosten,
            sonstiges: newSonstiges // Neues Array
        });
    };
    const addBaustellennebenkosten = () => {
        const newKosten = [...baustellennebenkosten, { bezeichnung: '', kosten: 0 }];
        setBaustellennebenkosten(newKosten);
        // Kein direkter Trigger, Update erfolgt bei Eingabe
    };
    const addSonstiges = () => {
        const newSonstiges = [...sonstiges, { bezeichnung: '', kosten: 0 }];
        setSonstiges(newSonstiges);
        // Kein direkter Trigger
    };
    const removeBaustellennebenkosten = (index) => {
        const newKosten = baustellennebenkosten.filter((_, i) => i !== index);
        const finalKosten = newKosten.length > 0 ? newKosten : [{ bezeichnung: '', kosten: 0 }];
        setBaustellennebenkosten(finalKosten);
        prepareAndUpdate({
            stundenverrechnungssatz,
            arbeitszeit,
            baustellennebenkosten: finalKosten, // Neues Array
            sonstiges
        });
    };
    const removeSonstiges = (index) => {
        const newSonstiges = sonstiges.filter((_, i) => i !== index);
        const finalSonstiges = newSonstiges.length > 0 ? newSonstiges : [{ bezeichnung: '', kosten: 0 }];
        setSonstiges(finalSonstiges);
        prepareAndUpdate({
            stundenverrechnungssatz,
            arbeitszeit,
            baustellennebenkosten,
            sonstiges: finalSonstiges // Neues Array
        });
    };

    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownloadPdf = async () => {
        try {
            setIsDownloading(true);
            await dispatch(actions.downloadAngebotskalkulationPdf(projectId));
        } catch (error) {
            console.error('Fehler beim PDF-Download:', error);
            // Hier könnte man noch eine Fehlermeldung anzeigen
        } finally {
            setIsDownloading(false);
        }
    };

    // --- Render Logic ---
    if (!project || !project.id) {
        return (
            <div className="container-fluid"><br /><h3>{t('project_calculation_offer_positions_title')}</h3><hr /><p>{t('project_calculation_loading_project_data', 'Lade Projektdaten...')}</p></div>
        );
    }

    return (
        <div className="container-fluid">
            <br />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>{t('project_calculation_headline_offer_positions','Angebotspositionen')}</h3>
                <button 
                    onClick={handleDownloadPdf}
                    className="btn btn-primary dashboard-button"
                    style={{ 
                        backgroundColor: '#f69c00',
                        color: '#fff',
                        borderColor: '#f69c00',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        transition: 'all 0.2s ease-in-out',
                        fontWeight: '500',
                        opacity: isDownloading ? 0.7 : 1,
                        cursor: isDownloading ? 'wait' : 'pointer'
                    }}
                    disabled={isDownloading}
                >
                    <i className="material-icons" style={{ fontSize: '20px', color: '#fff' }}>{isDownloading ? 'hourglass_empty' : 'download'}</i>
                    {isDownloading ? t('project_calculation_downloading_pdf', 'Wird heruntergeladen...') : t('project_calculation_download_pdf', 'Angebots-PDF')}
                </button>
            </div>

            {(!positions || positions.length === 0) && (
                <p>{t('project_calculation_no_positions_available', 'Keine Positionen für dieses Projekt verfügbar.')}</p>
            )}

            {/* Tabelle wird immer gerendert, um den tfoot anzuzeigen */}
            <table className="table table-hover">
                 {(positions && positions.length > 0) && (
                     <thead style={{ backgroundColor: '#f8f9fa' }}>
                        <tr className="small">
                            <th>{t('project_calculation_col_number','Nr.')}</th>
                            <th>{t('project_calculation_col_article','Artikel')}</th>
                            <th style={numberCellStyle}>{t('project_calculation_col_estimated_units','Gepl. Einh.')}<br /> [{t('project_calculation_col_piece','Stück')}]</th>
                            <th style={numberCellStyle}>{t('project_calculation_col_calculated_units','Ber. Einh.')}<br /> [{t('project_calculation_col_piece','Stück')}]</th>
                            <th style={numberCellStyle}>{t('project_calculation_col_unit_price','EP')}<br /> [€]</th>
                            <th style={numberCellStyle}>{t('project_calculation_col_discount','Rabatt')}<br /> [%]</th>
                            <th style={numberCellStyle}>{t('project_calculation_col_total_price','Gesamt')}<br /> [€]</th>
                        </tr>
                    </thead>
                 )}
                 {(positions && positions.length > 0) && (
                     <tbody>
                        {sortedGroupEntries.map(([group, groupPositions]) => (
                            <React.Fragment key={group}>
                                <tr className="small">
                                    <td colSpan="7" style={{ background: '#e9ecef', fontWeight: 'bold' }}>
                                        {t(`grouping_headline_${group}`, group)}
                                    </td>
                                </tr>
                                {groupPositions
                                    .slice()
                                    .sort((a, b) => { // Sortierung innerhalb der Gruppe
                                        const aVal = isNaN(Number(a.alphasort2)) ? String(a.alphasort2) : Number(a.alphasort2);
                                        const bVal = isNaN(Number(b.alphasort2)) ? String(b.alphasort2) : Number(b.alphasort2);
                                        if (aVal < bVal) return -1; if (aVal > bVal) return 1; return 0;
                                     })
                                    .map((position, index) => (
                                        <PositionRow
                                            key={position.id || `pos-${index}`}
                                            position={position}
                                            index={index}
                                            getValue={getValue}
                                            handleChange={handleChange}
                                        />
                                    ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                 )}
                 <tfoot className="small">
                    {/* Materialkosten nur anzeigen, wenn Positionen vorhanden sind */}
                    {positions && positions.length > 0 && (
                        <tr className="font-weight-bold" style={{ backgroundColor: '#f8f9fa' }}>
                            <td colSpan="6" className="text-right">{t('project_calculation_label_material_costs','Materialkosten')} (<SafeNumber value={totals.totalDiscount} decimals={1} /> %)</td>
                            <td style={rowsumCellStyle}><SafeNumber value={totals.totalSum} /></td>
                        </tr>
                    )}
                    {/* Kalkulationsdetails */}
                    <tr>
                        <td colSpan="2" className="text-right">{t('project_calculation_label_hourly_rate','Stundensatz')} [€]</td>
                        <td style={inputCellWithOwnAlignmentStyle}> <input type="number" min="0" step="0.01" value={stundenverrechnungssatz} onChange={handleStundenverrechnungssatzChange} placeholder="0,00" style={costInputStyle} /></td>
                        <td className="text-right">{t('project_calculation_label_working_time','Arbeitszeit')} [Min]</td>
                        <td style={inputCellWithOwnAlignmentStyle}> <input type="number" min="0" value={arbeitszeit} onChange={handleArbeitszeitChange} placeholder="0" style={costInputStyle} /></td>
                        <td className="text-right">{t('project_calculation_label_labor_costs','Lohnkosten')} [€]</td>
                        <td style={{ ...rowsumCellStyle }}><SafeNumber value={lohnkosten} /></td>
                    </tr>
                    {/* Baustellennebenkosten */}
                    <tr>
                        <td colSpan="2" className="text-right">{t('project_calculation_label_site_ancillary_costs','Baustellen-Nebenkosten')} [€]</td>
                        <td colSpan="4" style={tableCellStyleForTextLeft}>
                            {baustellennebenkosten.map((item, index) => (
                                <div key={`sitecost-desc-${index}`} style={{ display: 'flex', alignItems: 'center', marginBottom: baustellennebenkosten.length > 1 || index < baustellennebenkosten.length -1  ? '10px' : '0' }}>
                                    <input type="text" value={item.bezeichnung} onChange={(e) => handleBaustellennebenkostenChange(index, 'bezeichnung', e.target.value)} placeholder={t('project_calculation_label_description','Beschreibung')} style={descriptionInputStyle} />
                                    <span onClick={baustellennebenkosten.length > 1 ? () => removeBaustellennebenkosten(index) : undefined} className={`add-remove-handles ${baustellennebenkosten.length > 1 ? "hover-red" : ""}`} style={{ ...iconSpanStyle, opacity: baustellennebenkosten.length > 1 ? 1 : 0.5, cursor: baustellennebenkosten.length > 1 ? "pointer" : "default" }}><i className="material-icons icon-gray" style={iconStyle}>remove_circle</i></span>
                                    {index === baustellennebenkosten.length - 1 && (<span onClick={addBaustellennebenkosten} className="add-remove-handles hover-green" style={iconSpanStyle}><i className="material-icons icon-gray" style={iconStyle}>add_circle</i></span>)}
                                    {index !== baustellennebenkosten.length -1 && (<span style={{width: "40px"}}></span>) }
                                </div>
                            ))}
                        </td>
                        <td style={inputCellWithOwnAlignmentStyle}>
                            {baustellennebenkosten.map((item, index) => (
                                <div key={`sitecost-amount-${index}`} style={{ marginBottom: baustellennebenkosten.length > 1 || index < baustellennebenkosten.length -1 ? '10px' : '0', display: 'flex', justifyContent: 'flex-end' }}>
                                    <input type="number" min="0" step="0.01" value={item.kosten} onChange={(e) => handleBaustellennebenkostenChange(index, 'kosten', e.target.value)} placeholder={t('project_calculation_label_costs_placeholder','Kosten')} style={costInputStyle}/>
                                </div>
                            ))}
                        </td>
                    </tr>
                    {/* Sonstige Kosten */}
                    <tr>
                        <td colSpan="2" className="text-right">{t('project_calculation_label_other','Sonstige Kosten')} [€]</td>
                        <td colSpan="4" style={tableCellStyleForTextLeft}>
                            {sonstiges.map((item, index) => (
                                <div key={`othercost-desc-${index}`} style={{ display: 'flex', alignItems: 'center', marginBottom: sonstiges.length > 1 || index < sonstiges.length -1 ? '10px' : '0' }}>
                                    <input type="text" value={item.bezeichnung} onChange={(e) => handleSonstigesChange(index, 'bezeichnung', e.target.value)} placeholder={t('project_calculation_label_description','Beschreibung')} style={descriptionInputStyle} />
                                    <span onClick={sonstiges.length > 1 ? () => removeSonstiges(index) : undefined} className={`add-remove-handles ${sonstiges.length > 1 ? "hover-red" : ""}`} style={{ ...iconSpanStyle, opacity: sonstiges.length > 1 ? 1 : 0.5, cursor: sonstiges.length > 1 ? "pointer" : "default" }}><i className="material-icons icon-gray" style={iconStyle}>remove_circle</i></span>
                                    {index === sonstiges.length - 1 && (<span onClick={addSonstiges} className="add-remove-handles hover-green" style={iconSpanStyle}><i className="material-icons icon-gray" style={iconStyle}>add_circle</i></span>)}
                                    {index !== sonstiges.length -1 && (<span style={{width: "40px"}}></span>) }
                                </div>
                            ))}
                        </td>
                        <td style={inputCellWithOwnAlignmentStyle}>
                            {sonstiges.map((item, index) => (
                                <div key={`othercost-amount-${index}`} style={{ marginBottom: sonstiges.length > 1 || index < sonstiges.length -1 ? '10px' : '0', display: 'flex', justifyContent: 'flex-end' }}>
                                    <input type="number" min="0" step="0.01" value={item.kosten} onChange={(e) => handleSonstigesChange(index, 'kosten', e.target.value)} placeholder={t('project_calculation_label_costs_placeholder','Kosten')} style={costInputStyle} />
                                </div>
                            ))}
                        </td>
                    </tr>
                    {/* Gesamtsummen */}
                    <tr className="font-weight-bold" style={{ backgroundColor: '#f8f9fa' }}>
                        <td colSpan="6" className="text-right">{t('project_calculation_label_assembly_costs','Montagekosten')} [€]</td>
                        <td style={rowsumCellStyle}><SafeNumber value={montagekosten} /></td>
                    </tr>
                    <tr className="font-weight-bold" style={{ backgroundColor: '#f8f9fa' }}>
                        <td colSpan="6" className="text-right">{t('project_calculation_label_total_costs','Gesamtkosten Angebot')} [€]</td>
                        <td style={rowsumCellStyle}><SafeNumber value={gesamtkosten} /></td>
                    </tr>
                 </tfoot>
            </table>
        </div>
    );
};

ProjectOfferPositions.propTypes = { projectId: PropTypes.string.isRequired };
export default ProjectOfferPositions;

const styles = `
    .dashboard-button:hover {
        background-color: #999 !important;
        border-color: #999 !important;
    }
`;

// Füge die Styles zum DOM hinzu
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);



// WEBPACK FOOTER //
// ./src/components/Project/ProjectOfferPositions.js